import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';

function FolderTree({ folders, selected, onSelect }) {
  const roots = folders.filter((f) => !f.parent_id);
  const getChildren = (pid) => folders.filter((f) => f.parent_id === pid);

  function renderFolder(folder, level = 0) {
    const children = getChildren(folder.id);
    return (
      <li key={folder.id} className="py-0.5">
        <div onClick={() => onSelect(folder)}
          className={`flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer transition-colors
            ${selected?.id === folder.id ? 'bg-brand/[0.08] text-brand font-medium' : 'text-ink-secondary hover:bg-surface-inset'}`}
          style={{ paddingLeft: `${8 + level * 16}px` }}>
          {children.length > 0 ? '▸' : '·'} {folder.name}
        </div>
        {children.length > 0 && <ul className="list-none ml-4">{children.map((c) => renderFolder(c, level + 1))}</ul>}
      </li>
    );
  }

  return <ul className="list-none">{roots.map((f) => renderFolder(f))}</ul>;
}

export default function Folders() {
  const { isPrivileged } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPermModal, setShowPermModal] = useState(false);
  const [form, setForm] = useState({ name: '', parent_id: '' });
  const [permForm, setPermForm] = useState([]);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => { loadFolders(); }, []);
  async function loadFolders() { const { data } = await api.get('/folders'); setFolders(data.data); }
  async function loadGroups() { try { const { data } = await api.get('/groups'); setGroups(data.data); } catch {} }

  async function handleSave() {
    setError('');
    try { await api.post('/folders', { name: form.name, parent_id: form.parent_id || null }); setShowModal(false); loadFolders(); }
    catch (err) { setError(err.response?.data?.error || 'Erro'); }
  }
  async function handleDelete() {
    if (!selected || !window.confirm('Excluir?')) return;
    try { await api.delete(`/folders/${selected.id}`); setSelected(null); loadFolders(); } catch (err) { alert(err.response?.data?.error); }
  }
  async function savePermissions() {
    try { await api.put(`/folders/${selected.id}/permissions`, { permissions: permForm.filter((p) => p.user_group_id) }); setShowPermModal(false); loadFolders(); }
    catch (err) { alert(err.response?.data?.error); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold tracking-tight">Pastas</h1>
        {isPrivileged && <button className="btn btn-primary" onClick={() => { setForm({ name: '', parent_id: selected?.id || '' }); setError(''); setShowModal(true); }}>Nova Pasta</button>}
      </div>

      <div className="grid grid-cols-[300px_1fr] gap-4">
        <div className="bg-surface-100 border rounded-md p-4">
          <h3 className="text-base font-semibold mb-3">Arvore de Pastas</h3>
          {folders.length === 0 ? <p className="text-sm text-ink-muted">Nenhuma pasta</p> : <FolderTree folders={folders} selected={selected} onSelect={setSelected} />}
        </div>
        <div className="bg-surface-100 border rounded-md p-4">
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-subtle">
                <h3 className="text-base font-semibold">{selected.name}</h3>
                {isPrivileged && <div className="flex gap-2">
                  <button className="btn btn-sm btn-secondary" onClick={() => { loadGroups(); setPermForm(selected.permissions?.map((p) => ({ user_group_id: p.user_group_id, permission: p.permission })) || []); setShowPermModal(true); }}>Permissões</button>
                  <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/documents?folder=${selected.id}`)}>Ver Documentos</button>
                  <button className="btn btn-sm btn-danger" onClick={handleDelete}>Excluir</button>
                </div>}
              </div>
              {selected.permissions?.length > 0 && (
                <table className="w-full border-collapse">
                  <thead><tr className="border-b"><th className="text-left text-2xs font-semibold text-ink-muted uppercase px-3 py-2">Grupo</th><th className="text-left text-2xs font-semibold text-ink-muted uppercase px-3 py-2">Permissão</th></tr></thead>
                  <tbody>{selected.permissions.map((p) => (
                    <tr key={p.id} className="border-b border-subtle"><td className="px-3 py-2">{p.group_name}</td><td className="px-3 py-2"><span className="doc-code">{p.permission}</span></td></tr>
                  ))}</tbody>
                </table>
              )}
            </>
          ) : <div className="text-center py-12 text-ink-muted">Selecione uma pasta para ver detalhes</div>}
        </div>
      </div>

      {showModal && (
        <Modal title="Nova Pasta" onClose={() => setShowModal(false)}>
          {error && <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>}
          <div className="mb-4"><label className="block text-sm font-medium text-ink-secondary mb-1">Nome</label>
            <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="mb-4"><label className="block text-sm font-medium text-ink-secondary mb-1">Pasta Pai</label>
            <select className="form-select" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
              <option value="">Raiz</option>{folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select></div>
          <div className="flex gap-3 justify-end mt-5">
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSave}>Criar</button>
          </div>
        </Modal>
      )}

      {showPermModal && (
        <Modal title="Permissões da Pasta" onClose={() => setShowPermModal(false)}>
          {permForm.map((p, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <select className="form-select" value={p.user_group_id}
                onChange={(e) => { const u = [...permForm]; u[i].user_group_id = e.target.value; setPermForm(u); }}>
                <option value="">Grupo...</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <select className="form-select w-36" value={p.permission}
                onChange={(e) => { const u = [...permForm]; u[i].permission = e.target.value; setPermForm(u); }}>
                <option value="editor">Editor</option><option value="manager">Manager</option><option value="total">Total</option>
              </select>
            </div>
          ))}
          <button className="btn btn-sm btn-secondary mb-3" onClick={() => setPermForm([...permForm, { user_group_id: '', permission: 'editor' }])}>+ Adicionar</button>
          <div className="flex gap-3 justify-end mt-5">
            <button className="btn btn-secondary" onClick={() => setShowPermModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={savePermissions}>Salvar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
